import React from 'react';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { useEhiOptions } from '@hooks/useEhiOptions.ts';
import type { TFunction } from 'i18next';
import { EhiBlockResult, EhiCalculationBreakdown, EhiCorridorThresholds, EhiSnapshotDetails, ehiService } from '@services/ehi.service.ts';
import { EhiDirectionsMatrixTable } from './EhiDirectionsMatrixTable';

interface Props {
  /** Який знімок показати; null - модалка закрита. */
  target: { userId: number; date: string } | null;
  onClose: () => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right">{value}</Typography>
  </>
);

type BlockKind = 'ratio' | 'corridor' | 'selfAssessment';

interface BlockScales {
  corridor: EhiCorridorThresholds;
  ratingScale: EhiSnapshotDetails['ratingScale'];
}

const round2 = (value: number) => Math.round(value * 100) / 100;
const percent = (share: number) => round2(share * 100);

/** "How it was computed" for one block, in numbers of this snapshot. */
const blockFormula = (
  kind: BlockKind,
  block: EhiBlockResult,
  scales: BlockScales,
  t: TFunction,
): string => {
  const { corridor, ratingScale } = scales;
  if (kind === 'selfAssessment') {
    if (block.fact === null) return t('ehiJournal.formulaNoSelfAssessment');
    const rating = round2(block.fact / ratingScale.stepPercent + ratingScale.min);
    return t('ehiJournal.formulaSelfAssessment', { rating, min: ratingScale.min, max: ratingScale.max, step: ratingScale.stepPercent, score: round2(block.score) });
  }
  if (block.fact === null) return t('ehiJournal.formulaNoFacts');
  const score = round2(block.score);
  if (kind === 'ratio') {
    const raw = round2((block.fact / block.target) * 100);
    return raw > 100
      ? t('ehiJournal.formulaRatioCapped', { fact: block.fact, target: block.target, raw })
      : t('ehiJournal.formulaRatio', { fact: block.fact, target: block.target, score });
  }
  const share = percent(block.fact / block.target);
  const c = { min: percent(corridor.fullRangeMin), max: percent(corridor.fullRangeMax), zeroBelow: percent(corridor.zeroBelow), zeroAbove: percent(corridor.zeroAbove) };
  const base = t('ehiJournal.formulaShare', { fact: block.fact, target: block.target, share });
  if (share >= c.min && share <= c.max) return `${base} ${t('ehiJournal.formulaCorridorFull', c)}`;
  if (share > c.zeroBelow && share < c.min) return `${base} ${t('ehiJournal.formulaCorridorBelow', { ...c, share, score })}`;
  if (share > c.max && share < c.zeroAbove) return `${base} ${t('ehiJournal.formulaCorridorAbove', { ...c, share, score })}`;
  return `${base} ${t('ehiJournal.formulaCorridorOut', c)}`;
};

/** One block row: fact / target / score and how the score was computed; "not applicable" when the block is null. */
const BlockRow: React.FC<{ label: string; block: EhiBlockResult | null; kind: BlockKind; scales: BlockScales; t: TFunction }> = ({ label, block, kind, scales, t }) => (
  <>
    <Typography variant="body2">{label}</Typography>
    {block ? (
      <>
        {/* Self-assessment blocks have no fact/target: "fact" is the ready score, target 100 only marks the block as applicable. */}
        <Typography variant="body2" textAlign="right">{kind === 'selfAssessment' ? '—' : block.fact ?? '—'}</Typography>
        <Typography variant="body2" textAlign="right">{kind === 'selfAssessment' ? '—' : block.target}</Typography>
        <Typography variant="body2" fontWeight={600} textAlign="right">{round2(block.score)}</Typography>
        <Typography variant="caption" color="text.secondary">{blockFormula(kind, block, scales, t)}</Typography>
      </>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ gridColumn: 'span 4' }}>{t('ehiJournal.blockNotApplicable')}</Typography>
    )}
  </>
);

const BreakdownTable: React.FC<{ breakdown: EhiCalculationBreakdown; scales: BlockScales; t: TFunction }> = ({ breakdown, scales, t }) => {
  const applied = [breakdown.activity, breakdown.feeling, breakdown.tasks, breakdown.nutrition, breakdown.water]
    .filter((b): b is EhiBlockResult => b !== null);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto repeat(3, auto) minmax(220px, 1fr)', rowGap: 0.75, columnGap: 2, alignItems: 'baseline' }}>
        <Typography variant="caption" color="text.secondary" />
        <Typography variant="caption" color="text.secondary" textAlign="right">{t('ehiJournal.colFact')}</Typography>
        <Typography variant="caption" color="text.secondary" textAlign="right">{t('ehiJournal.colTarget')}</Typography>
        <Typography variant="caption" color="text.secondary" textAlign="right">{t('ehiJournal.colBlockScore')}</Typography>
        <Typography variant="caption" color="text.secondary">{t('ehiJournal.colFormula')}</Typography>
        <BlockRow label={t('ehiJournal.blockWater')} block={breakdown.water} kind="ratio" scales={scales} t={t} />
        <BlockRow label={t('ehiJournal.blockNutrition')} block={breakdown.nutrition} kind="corridor" scales={scales} t={t} />
        <BlockRow label={t('ehiJournal.blockActivity')} block={breakdown.activity} kind="ratio" scales={scales} t={t} />
        <BlockRow label={t('ehiJournal.blockFeeling')} block={breakdown.feeling} kind="selfAssessment" scales={scales} t={t} />
        <BlockRow label={t('ehiJournal.blockTasks')} block={breakdown.tasks} kind="selfAssessment" scales={scales} t={t} />
        <Typography variant="body2" color="text.secondary">{t('ehiJournal.colDayScore')}</Typography>
        <Box sx={{ gridColumn: 'span 2' }} />
        <Typography variant="body2" fontWeight={600} textAlign="right">{round2(breakdown.score)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {`(${applied.map((b) => round2(b.score)).join(' + ')}) / ${applied.length} = ${round2(breakdown.score)}`}
        </Typography>
      </Box>
      {(breakdown.feeling || breakdown.tasks) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {t('ehiJournal.feelingTasksNote')}
        </Typography>
      )}
    </Box>
  );
};

// Деталі одного знімка: з чого складається цифра - розклад по блоках (факт/ціль/бал: вода, харчування, активність),
// збережений результат і живий перерахунок зараз (для порівняння/налагодження формул).
export const EhiSnapshotDetailsModal: React.FC<Props> = ({ target, onClose }) => {
  const { t } = useTranslation();
  const { levels } = useEhiOptions();
  const levelLabel = (v: string) => levels.find((o) => o.value === v)?.label ?? v;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ehi', 'snapshot-details', target?.userId, target?.date],
    queryFn: () => ehiService.getSnapshotDetails(target!.userId, target!.date),
    enabled: target !== null,
  });

  const stored = data?.snapshot;
  const breakdown = data?.breakdown;
  const recomputed = data?.recomputed;
  const matches = stored && recomputed ? stored.ehiValueDisplay === recomputed.ehiValueDisplay && stored.ehiLevel === recomputed.ehiLevel : null;

  return (
    <BaseDialog
      open={target !== null}
      onClose={onClose}
      title={t('ehiJournal.snapshotDetailsTitle')}
      iDoc={ModalIndices.ADMIN_EHI_SNAPSHOT_DETAILS}
      maxWidth="md"
    >
      {isLoading && <Box display="flex" justifyContent="center" sx={{ py: 3 }}><CircularProgress /></Box>}
      {isError && <Alert severity="error">{t('ehiJournal.detailsError')}</Alert>}
      {stored && data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>{stored.userName ?? stored.userEmail ?? `#${stored.userId}`}</Typography>
            <Typography variant="caption" color="text.secondary">{stored.userName ? `${stored.userEmail} · ` : ''}{stored.snapshotDate}</Typography>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionBreakdown')}</Typography>
            {breakdown ? (
              <BreakdownTable breakdown={breakdown} scales={{ corridor: data.nutritionCorridor.thresholds, ratingScale: data.ratingScale }} t={t} />
            ) : (
              <Typography variant="body2" color="text.secondary">{t('ehiJournal.noBreakdown')}</Typography>
            )}
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionStored')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
              <Row label={t('ehiJournal.colEhi')} value={`${stored.ehiValueDisplay} (${stored.ehiValueRaw})`} />
              <Row label={t('ehiJournal.colLevel')} value={levelLabel(stored.ehiLevel)} />
              <Row label={t('ehiJournal.colUpdated')} value={new Date(stored.updatedAt).toLocaleString()} />
            </Box>
          </Box>

          {/* 6 directions are an integral part of EHI (decision 2026-09-23). Matrix table when block scores are stored. */}
          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionDirections')}</Typography>
            {data.directionsBreakdown ? (
              <EhiDirectionsMatrixTable breakdown={data.directionsBreakdown} stored={stored.directionScores} />
            ) : stored.directionScores ? (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
                  <Row label={t('ehiJournal.directionPhysical')} value={Math.round(stored.directionScores.physical)} />
                  <Row label={t('ehiJournal.directionEnergy')} value={Math.round(stored.directionScores.energy)} />
                  <Row label={t('ehiJournal.directionClarity')} value={Math.round(stored.directionScores.clarity)} />
                  <Row label={t('ehiJournal.directionProductivity')} value={Math.round(stored.directionScores.productivity)} />
                  <Row label={t('ehiJournal.directionFocus')} value={Math.round(stored.directionScores.focus)} />
                  <Row label={t('ehiJournal.directionEmotional')} value={Math.round(stored.directionScores.emotional)} />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{t('ehiJournal.directionsNoMatrix')}</Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('ehiJournal.noDirections')}</Typography>
            )}
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">{t('ehiJournal.sectionRecomputed')}</Typography>
            {recomputed ? (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
                  <Row label={t('ehiJournal.colEhi')} value={`${recomputed.ehiValueDisplay} (${recomputed.ehiValueRaw})`} />
                  <Row label={t('ehiJournal.colLevel')} value={levelLabel(recomputed.ehiLevel)} />
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }} color="text.secondary">{recomputed.explanation}</Typography>
                <Chip
                  size="small"
                  sx={{ mt: 1 }}
                  color={matches ? 'success' : 'warning'}
                  label={matches ? t('ehiJournal.matches') : t('ehiJournal.differs')}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('ehiJournal.noRecompute')}</Typography>
            )}
          </Box>
        </Box>
      )}
    </BaseDialog>
  );
};
